import React, { useState, useEffect } from 'react';
import { Calendar, Info, RotateCcw, Pencil, X } from 'lucide-react';
import { getRiderRides, cancelRide, rescheduleRide } from '../../services/rideService';
import { authStorage } from '../../utils/storage';
import { toast } from '../../hooks/use-toast';
import ScheduleRideModal from './ScheduleRideModal';
import PawaPayHistory from '../shared/PawaPayHistory';

const YachanguRidesPage = () => {
  const [activeTab, setActiveTab] = useState('past');
  const [rides, setRides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [editingRide, setEditingRide] = useState(null);
  const [cancellingRide, setCancellingRide] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadRides();
  }, []);

  const loadRides = async () => {
    setIsLoading(true);
    try {
      const authData = await authStorage.getAuth();
      if (authData?.user?.id) {
        setUserId(authData.user.id);
        const result = await getRiderRides(authData.user.id, 100, true); // Get all statuses
        if (result.success) {
          setRides(result.data || result.rides || []);
        }
      }
    } catch (error) {
      console.error('Error loading rides:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReschedule = async (newDate) => {
    if (!editingRide) return;
    setActionLoading(true);
    const res = await rescheduleRide(editingRide.id, newDate.toISOString(), userId);
    setActionLoading(false);
    if (res.success) {
      toast({ title: 'Ride rescheduled ✓', description: 'Your pickup time has been updated.' });
      setEditingRide(null);
      loadRides();
    } else {
      toast({ title: 'Could not reschedule', description: res.message, variant: 'destructive' });
    }
  };

  const handleCancel = async () => {
    if (!cancellingRide) return;
    setActionLoading(true);
    const res = await cancelRide(cancellingRide.id, userId, 'rider_cancelled');
    setActionLoading(false);
    if (res.success) {
      toast({ title: 'Scheduled ride cancelled' });
      setCancellingRide(null);
      loadRides();
    } else {
      toast({ title: 'Could not cancel', description: res.message, variant: 'destructive' });
    }
  };

  // Group rides by month
  const groupRidesByMonth = (rides) => {
    const grouped = {};
    rides.forEach(ride => {
      const date = new Date(ride.created_at || ride.createdAt);
      const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(ride);
    });
    return grouped;
  };

  const pastRides = rides.filter(r => 
    ['completed', 'cancelled'].includes(r.status)
  );
  const upcomingRides = rides.filter(r => 
    ['pending', 'accepted', 'picked_up', 'scheduled'].includes(r.status)
  );

  const displayRides = activeTab === 'past' ? pastRides : upcomingRides;
  const groupedRides = groupRidesByMonth(displayRides);

  return (
    <div className="h-screen bg-green-50 dark:bg-gray-900 overflow-y-auto pb-20 transition-colors">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-green-100 dark:border-gray-700 z-10 transition-colors">
        <div className="flex items-center justify-between p-6 pb-4">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">History</h1>
          <button className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-400">
            <Info className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex px-6 space-x-8">
          <button
            onClick={() => setActiveTab('past')}
            data-testid="rides-tab-past"
            className={`pb-3 font-medium transition-colors relative ${
              activeTab === 'past' ? 'text-gray-900 dark:text-white' : 'text-gray-400'
            }`}
          >
            Past
            {activeTab === 'past' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            data-testid="rides-tab-upcoming"
            className={`pb-3 font-medium transition-colors relative ${
              activeTab === 'upcoming' ? 'text-gray-900 dark:text-white' : 'text-gray-400'
            }`}
          >
            Upcoming
            {activeTab === 'upcoming' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            data-testid="rides-tab-payments"
            className={`pb-3 font-medium transition-colors relative ${
              activeTab === 'payments' ? 'text-gray-900 dark:text-white' : 'text-gray-400'
            }`}
          >
            Payments
            {activeTab === 'payments' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500"></div>
            )}
          </button>
        </div>
      </div>

      {/* Payments history */}
      {activeTab === 'payments' ? (
        <div className="px-6 py-4">
          <PawaPayHistory userId={userId} title="Mobile-money payments" />
        </div>
      ) : (
      /* Rides List */
      <div className="px-6 py-4">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-green-100 border-t-green-500 rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading history...</p>
          </div>
        ) : Object.keys(groupedRides).length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-green-200 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No {activeTab} rides</p>
          </div>
        ) : (
          Object.entries(groupedRides).map(([month, monthRides]) => (
            <div key={month} className="mb-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">{month}</h2>
              <div className="space-y-3">
                {monthRides.map((ride) => {
                  const isScheduled = ride.status === 'scheduled';
                  const date = new Date(
                    isScheduled
                      ? (ride.scheduled_time || ride.scheduledTime || ride.created_at || ride.createdAt)
                      : (ride.created_at || ride.createdAt)
                  );
                  const timeStr = date.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: false 
                  });
                  const dateStr = date.toLocaleDateString('en-US', { 
                    day: 'numeric', 
                    month: 'short' 
                  });

                  return (
                    <div
                      key={ride.id || ride._id}
                      className="bg-white border border-green-100 rounded-2xl p-4 hover:border-green-200 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          {/* Icon - Different for scheduled rides */}
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1 ${
                            isScheduled ? 'bg-blue-50' : 'bg-green-50'
                          }`}>
                            {isScheduled ? (
                              <Calendar className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Calendar className="w-5 h-5 text-green-600" />
                            )}
                          </div>

                          {/* Ride Details */}
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <div className="text-sm text-gray-500">
                                {isScheduled ? 'Scheduled for ' : ''}{dateStr} · {timeStr}
                              </div>
                              {isScheduled && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                  Scheduled
                                </span>
                              )}
                            </div>
                            <div className="text-base font-medium text-gray-900 dark:text-white">
                              {ride.dropoff_location?.address || 
                               ride.dropoffLocation?.address || 
                               'Destination'}
                            </div>
                            <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                              MWK {(ride.estimated_price || ride.estimatedPrice || 0).toLocaleString()}
                            </div>

                            {/* Actions for scheduled rides: edit time / cancel */}
                            {isScheduled && (
                              <div className="flex items-center gap-2 mt-3">
                                <button
                                  onClick={() => setEditingRide(ride)}
                                  data-testid={`scheduled-edit-${ride.id}`}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors"
                                >
                                  <Pencil className="w-4 h-4" /> Edit time
                                </button>
                                <button
                                  onClick={() => setCancellingRide(ride)}
                                  data-testid={`scheduled-cancel-${ride.id}`}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 transition-colors"
                                >
                                  <X className="w-4 h-4" /> Cancel
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Repeat Icon (non-scheduled only) */}
                        {!isScheduled && (
                          <button className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 flex-shrink-0">
                            <RotateCcw className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
      )}

      {/* Edit time modal (reuses the scheduler) */}
      <ScheduleRideModal
        isOpen={!!editingRide}
        onClose={() => setEditingRide(null)}
        onSchedule={handleReschedule}
      />

      {/* Cancel confirmation */}
      {cancellingRide && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-6" data-testid="scheduled-cancel-confirm">
          <div className="absolute inset-0 bg-black/50" onClick={() => !actionLoading && setCancellingRide(null)} />
          <div className="relative w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900">Cancel scheduled ride?</h3>
            <p className="text-sm text-gray-600 mt-1">
              This will remove your ride scheduled for{' '}
              {new Date(cancellingRide.scheduled_time || cancellingRide.scheduledTime || cancellingRide.created_at)
                .toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}.
              No cancellation fee applies.
            </p>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setCancellingRide(null)}
                disabled={actionLoading}
                data-testid="scheduled-cancel-keep"
                className="flex-1 h-11 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Keep ride
              </button>
              <button
                onClick={handleCancel}
                disabled={actionLoading}
                data-testid="scheduled-cancel-confirm-btn"
                className="flex-1 h-11 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-500 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Cancelling…' : 'Cancel ride'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YachanguRidesPage;
